import React from 'react';
import { format, startOfYear, endOfYear, eachMonthOfInterval, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FilePdf } from '@phosphor-icons/react';
import { getCalendarDayStyle, getTaskCountColor } from '../../utils/styleHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
const DAY_HEADERS = ['L','M','M','J','V','S','D'];

const MonthGrid = ({ monthDate, yearTaskMap, onDayHover, onDayLeave, onDayClick }) => {
  const mStart = startOfMonth(monthDate);
  const mEnd = endOfMonth(monthDate);
  const days = eachDayOfInterval({ start: mStart, end: mEnd });
  const firstDayOfWeek = (getDay(mStart) + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  days.forEach(d => cells.push(d));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
      {DAY_HEADERS.map((h, i) => (
        <div key={`${h}-${i}`} style={{ textAlign: 'center', fontSize: '0.6rem', fontWeight: '700', color: '#5E5F66', padding: '2px 0' }}>{h}</div>
      ))}
      {cells.map((day, i) => {
        if (!day) return <div key={`empty-${i}`} />;
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayTasks = yearTaskMap[dateStr] || [];
        const hasTasks = dayTasks.length > 0;
        const today = isToday(day);
        const isWeekend = getDay(day) === 0 || getDay(day) === 6;
        const dayStyle = getCalendarDayStyle(today, hasTasks, isWeekend);

        return (
          <div
            key={dateStr}
            data-testid={`annual-day-${dateStr}`}
            onMouseEnter={() => hasTasks && onDayHover(dateStr, dayTasks)}
            onMouseLeave={onDayLeave}
            onClick={() => hasTasks && onDayClick(day)}
            style={{
              width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.65rem', borderRadius: '3px', cursor: hasTasks ? 'pointer' : 'default',
              position: 'relative',
              backgroundColor: dayStyle.backgroundColor,
              color: dayStyle.color,
              fontWeight: hasTasks || today ? '700' : '400',
              border: hasTasks ? '1px solid #90CAF9' : '1px solid transparent',
              transition: 'all 0.15s'
            }}
          >
            {day.getDate()}
            {hasTasks && (
              <div style={{
                position: 'absolute', bottom: '1px', left: '50%', transform: 'translateX(-50%)',
                width: '4px', height: '4px', borderRadius: '50%',
                backgroundColor: getTaskCountColor(dayTasks.length)
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DayTooltip = ({ hoveredDay }) => {
  if (!hoveredDay) return null;
  return (
    <div data-testid="day-tooltip" style={{
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      backgroundColor: 'white', border: '1px solid #E2E2E6', borderRadius: '6px',
      padding: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 100,
      maxWidth: '320px', minWidth: '240px'
    }}>
      <div style={{ fontWeight: '700', marginBottom: '8px', fontSize: '0.9rem' }}>
        {format(new Date(hoveredDay.dateStr), 'EEEE dd MMMM yyyy', { locale: fr })}
      </div>
      {hoveredDay.tasks.map((t, i) => (
        <div key={t.id || `task-${t.user_name}-${i}`} style={{ padding: '6px 8px', backgroundColor: '#E3F2FD', borderRadius: '4px', marginBottom: '4px', fontSize: '0.8rem' }}>
          <div style={{ fontWeight: '600' }}>{t.title}</div>
          <div style={{ color: '#5E5F66', fontSize: '0.75rem' }}>{t.user_name} - {t.start_time}-{t.end_time}</div>
          {t.address && <div style={{ color: '#5E5F66', fontSize: '0.7rem' }}>{t.address}</div>}
        </div>
      ))}
    </div>
  );
};

const CalendarLegend = () => (
  <div style={{ marginTop: '16px', display: 'flex', gap: '20px', fontSize: '0.8rem', color: '#5E5F66' }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#1565C0', display: 'inline-block' }} /> 1 tache
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#EF6C00', display: 'inline-block' }} /> 2 taches
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#C62828', display: 'inline-block' }} /> 3+ taches
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '12px', height: '12px', borderRadius: '4px', backgroundColor: 'var(--primary, #002FA7)', display: 'inline-block' }} /> Aujourd'hui
    </span>
  </div>
);

export const AnnualCalendarView = ({ selectedYear, setSelectedYear, yearTasks, yearTaskMap, yearLoading, hoveredDay, setHoveredDay, onDayClick }) => {
  const yearStart = startOfYear(new Date(selectedYear, 0, 1));
  const yearEnd = endOfYear(yearStart);
  const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

  return (
    <>
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedYear(y => y - 1)}>{selectedYear - 1}</button>
          <span style={{ fontWeight: '700', fontSize: '1.2rem' }} data-testid="selected-year">{selectedYear}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedYear(y => y + 1)}>{selectedYear + 1}</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedYear(new Date().getFullYear())}>Annee en cours</button>
          <span style={{ fontSize: '0.85rem', color: '#5E5F66', marginLeft: 'auto' }}>
            {yearTasks.length} tache(s) planifiees en {selectedYear}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => window.open(`${API_URL}/api/planning/export/annual-pdf?year=${selectedYear}`, '_blank')} data-testid="export-annual-pdf-btn">
            <FilePdf size={16} /> PDF Annuel
          </button>
        </div>
      </div>

      {yearLoading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', position: 'relative' }}>
          {months.map((monthDate, idx) => (
            <div key={`month-${MONTH_NAMES_FR[idx]}`} className="card" style={{ padding: '8px 10px', border: '1.5px solid #C5C7CD', borderRadius: 8 }}>
              <div style={{ fontWeight: '700', fontSize: '0.78rem', marginBottom: '6px', color: 'var(--primary, #002FA7)', textAlign: 'center', borderBottom: '1px solid #E2E2E6', paddingBottom: 4 }}>
                {MONTH_NAMES_FR[idx]}
              </div>
              <MonthGrid
                monthDate={monthDate}
                yearTaskMap={yearTaskMap}
                onDayHover={(dateStr, tasks) => setHoveredDay({ dateStr, tasks })}
                onDayLeave={() => setHoveredDay(null)}
                onDayClick={onDayClick}
              />
            </div>
          ))}
          <DayTooltip hoveredDay={hoveredDay} />
        </div>
      )}

      <CalendarLegend />
    </>
  );
};
